import * as THREE from 'three';
import { fbm, worley, clamp01, smoothstep } from '../core/Noise.js';
import { MASK } from '../world/Physics.js';
import { particleVert, particleFrag } from './shaders/particle.glsl.js';

/**
 * The particle engine.
 *
 * Three cooperating pools, chosen per effect for what it actually needs:
 *
 *  1. `soft`  — alpha-blended, normal-mapped, depth-faded sprites. Smoke, dust,
 *               blood mist. Simulated on the GPU from baked spawn attributes.
 *  2. `add`   — additively-blended sprites, optionally stretched along velocity.
 *               Sparks, flashes, fireballs, glow. Same GPU simulation.
 *  3. `chips` / `shards` / `shells` — genuine 3D geometry in InstancedMeshes,
 *               integrated on the CPU against the collision world so they bounce.
 *               Small counts, real lighting, real tumble.
 *
 * The split is deliberate. Billboards are cheap but they are always flat and always
 * face you; a concrete chip that catches the sun and skitters off the floor cannot
 * be faked with one. Conversely, simulating ten thousand smoke puffs on the CPU is
 * a waste when a closed-form trajectory gets you the same picture for free.
 *
 * Allocation policy: every buffer, vector, matrix and pool entry is allocated in the
 * constructor. `update()` and every `spawn()` path allocate nothing.
 */

export const TUNING = {
  /* pool split of ctx.quality.maxParticles */
  softShare: 0.55,
  addShare: 0.45,

  /* CPU-physics debris */
  chipCount: 160,
  shardCount: 96,
  shellCount: 24,
  emberCount: 110,
  debrisGravity: 22, // exaggerated; small light objects read as floaty at 9.81
  debrisRaycastsPerFrame: 72,

  /* soft particles */
  // Metres of depth over which a sprite fades into geometry. Impact dust is born
  // centimetres from a wall, so a generous fade distance erases the very effect it
  // is meant to soften. Small enough to hide the intersection, no larger.
  softDistance: 0.26,
  nearFadeStart: 0.18,
  nearFadeEnd: 1.1,
  // 2.4 rather than 1.5. The spark sprite's alpha across its width is `x^2.2`, so
  // only the very centre of the capsule is opaque; hold the quad to a pixel and a
  // half and the rasteriser samples that peak intermittently, which is what turns a
  // long streak into a dotted line. Two and a half pixels is the width at which
  // every scanline along the streak catches some of the core.
  minSparkPixels: 2.4,
  // Scene depth prepass runs at 1/N resolution. At 1/3 the stair-steps in the
  // depth edge show through as blocky terraces in the smoke fade; 1/2 is the point
  // where they stop being visible and is still cheap, since the pass is depth-only.
  depthScale: 2,
  // Frames between depth prepasses. The pass costs ~0.8 ms on a full level — the
  // largest single item in the FX budget — and the particles already read a
  // one-frame-old target, so refreshing it every other frame halves that for no
  // visible difference: a soft fade is a low-frequency spatial signal and the
  // camera cannot move far enough in 16 ms to expose the lag.
  depthInterval: 3,

  atlasCell: 128,
};

/** Atlas cell indices. Cells 0 and 1 store a billow normal in RG and density in B. */
export const CELL = {
  SMOKE: 0,
  DUST: 1,
  GLOW: 2,
  SPARK: 3,
  BLOOD: 4,
  RING: 5,
  FLECK: 6,
  WISP: 7,
};

const ATLAS_COLS = 4;
const ATLAS_ROWS = 2;

/* ========================================================================== *
 * Procedural sprite atlas
 * ========================================================================== */

/**
 * Builds the sprite sheet. Two of the eight cells are *normal maps* rather than
 * colour: a smoke puff is authored as a billowing height field, and the shader
 * lights it. That single decision is most of the difference between smoke that
 * looks like a volume and smoke that looks like a grey stain.
 */
export function buildParticleAtlasData(cell) {
  const W = ATLAS_COLS * cell;
  const H = ATLAS_ROWS * cell;
  const data = new Uint8ClampedArray(W * H * 4);
  const h = new Float32Array(cell * cell);
  const a = new Float32Array(cell * cell);

  const write = (idx, x, y, r, g, b, al) => {
    const cx = (idx % ATLAS_COLS) * cell + x;
    const cy = Math.floor(idx / ATLAS_COLS) * cell + y;
    const o = (cy * W + cx) * 4;
    data[o] = r * 255;
    data[o + 1] = g * 255;
    data[o + 2] = b * 255;
    data[o + 3] = al * 255;
  };

  /* ---- billowing height fields (smoke, dust) -> normal + density ---- */

  /**
   * A puff is authored as a *dome* with a few large lobes rolled into it, not as
   * high-frequency noise. That ratio is the whole trick: the dome is what makes the
   * sprite light like a ball of material, and the lobes are what stop it looking
   * like a perfect sphere. Fine noise here would read as crumpled foil — the
   * shading detail must be an order of magnitude coarser than the texel grid.
   *
   * Everything is kept inside a margin so no puff touches its atlas cell border;
   * bilinear filtering across a cell edge shows up as a hard line on screen.
   */
  const billow = (idx, seed, octaves, warp, edge, contrast, lobeFreq, lobeAmp) => {
    for (let y = 0; y < cell; y++) {
      for (let x = 0; x < cell; x++) {
        const u = (x + 0.5) / cell;
        const v = (y + 0.5) / cell;
        const dx = u - 0.5;
        const dy = v - 0.5;
        const r = Math.sqrt(dx * dx + dy * dy) * 2;

        // Domain-warped fbm gives lobes that read as separate rolls of smoke
        // rather than a uniform cloud.
        const wu = u + (fbm(u * 3, v * 3, 3, 2, seed + 11) - 0.5) * warp;
        const wv = v + (fbm(u * 3 + 5, v * 3 + 5, 3, 2, seed + 23) - 0.5) * warp;
        const n = fbm(wu * lobeFreq, wv * lobeFreq, lobeFreq, octaves, seed);

        // Radial mask keeps the sprite roughly circular; the noise eats into its
        // edge so the silhouette is ragged instead of a perfect disc. The outer
        // limit is 0.74 so even the noisiest edge stays clear of the cell border.
        const mask = clamp01(1 - smoothstep(edge, 0.74, r + (n - 0.5) * 0.30));
        const dens = clamp01(Math.pow(mask, contrast) * (0.6 + n * 0.55));

        const i = y * cell + x;
        a[i] = dens;
        const dome = Math.sqrt(clamp01(1 - r * r * 1.8));
        h[i] = dens * (dome * 0.85 + n * lobeAmp);
      }
    }
    // Sobel -> tangent-space normal. The gain is tuned so the dome contributes a
    // normal of roughly 0.3 at the silhouette: enough to shade, not enough to
    // turn the sprite into a crumpled ball.
    const at = (x, y) => h[Math.min(cell - 1, Math.max(0, y)) * cell + Math.min(cell - 1, Math.max(0, x))];
    const gain = cell * 0.062;
    for (let y = 0; y < cell; y++) {
      for (let x = 0; x < cell; x++) {
        const gx = at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1) - (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1));
        const gy = at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1) - (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1));
        let nx = gx * gain;
        let ny = -gy * gain;
        const nz = 1;
        const inv = 1 / Math.hypot(nx, ny, nz);
        nx *= inv;
        ny *= inv;
        const i = y * cell + x;
        write(idx, x, y, nx * 0.5 + 0.5, ny * 0.5 + 0.5, a[i], a[i]);
      }
    }
  };

  billow(CELL.SMOKE, 101, 3, 0.35, 0.30, 1.05, 3.5, 0.16);
  billow(CELL.DUST, 233, 3, 0.55, 0.10, 1.7, 4.5, 0.10);

  /* ---- plain sprites: RGB is a luminance mask, tinted by the particle ---- */
  for (let y = 0; y < cell; y++) {
    for (let x = 0; x < cell; x++) {
      const u = (x + 0.5) / cell;
      const v = (y + 0.5) / cell;
      const dx = u - 0.5;
      const dy = v - 0.5;
      const r = Math.sqrt(dx * dx + dy * dy) * 2;

      // GLOW: tight hot core in a wide soft falloff.
      const glow = Math.pow(clamp01(1 - r), 2.6) * 0.75 + Math.pow(clamp01(1 - r * 2.6), 3.0) * 0.9;
      write(CELL.GLOW, x, y, 1, 1, 1, clamp01(glow));

      // SPARK: a capsule with a blown-out centre line. Stretched by the shader.
      const across = clamp01(1 - Math.abs(dx) * 4.2);
      const along = clamp01(1 - Math.abs(dy) * 2.5);
      const spark = Math.pow(across, 2.2) * Math.pow(along, 1.1);
      write(CELL.SPARK, x, y, 1, 1, 1, clamp01(spark));

      // BLOOD: a lopsided teardrop, heavier at one end, with satellite specks.
      const wob = fbm(u * 5, v * 5, 5, 3, 401);
      const tearY = dy * (dy > 0 ? 1.05 : 1.9); // tail trailing to one side
      const tr = Math.sqrt(dx * dx * 1.35 + tearY * tearY) * 2;
      let blood = clamp01(1 - smoothstep(0.30, 0.52, tr + (wob - 0.5) * 0.34));
      const sat = worley(u * 8, v * 8, 8, 419);
      blood = Math.max(blood, clamp01(1 - smoothstep(0.015, 0.06, sat.f1)) * clamp01(1 - smoothstep(0.4, 0.78, r)) * 0.85);
      write(CELL.BLOOD, x, y, 1, 1, 1, clamp01(blood));

      // RING: thin shockwave annulus. A fat ring reads as a smoke donut.
      const ringN = fbm(Math.cos(Math.atan2(dy, dx)) * 2 + 9, Math.sin(Math.atan2(dy, dx)) * 2 + 9, 8, 2, 907);
      const band = clamp01(1 - Math.abs(r - (0.68 + (ringN - 0.5) * 0.06)) / 0.115);
      write(CELL.RING, x, y, 1, 1, 1, band * band);

      // FLECK: hard-edged irregular grit, for sand and pulverised material.
      const ga = Math.atan2(dy, dx);
      const poly = 0.30 + fbm(Math.cos(ga) * 2.5 + 3, Math.sin(ga) * 2.5 + 3, 8, 2, 733) * 0.26;
      write(CELL.FLECK, x, y, 1, 1, 1, r * 0.62 < poly ? 1 : 0);

      // WISP: soft directional smear for vapour and water throw-off.
      const wisp = Math.pow(clamp01(1 - Math.abs(dx) * 3.0), 2.0) * Math.pow(clamp01(1 - Math.abs(dy) * 2.2), 2.0) * (0.55 + fbm(u * 9, v * 4, 9, 2, 811) * 0.8);
      write(CELL.WISP, x, y, 1, 1, 1, clamp01(wisp));
    }
  }

  return { data, width: W, height: H };
}

function makeParticleAtlas(cell) {
  const { data, width, height } = buildParticleAtlasData(cell);
  const tex = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  // A stretched spark is the pathological case for isotropic mip selection: the
  // quad is three hundred pixels long and two wide, so the derivative along its
  // length picks a mip far coarser than its width needs and the streak breaks into
  // a dotted line. Anisotropic filtering is what turns those dashes back into a
  // continuous trail, and it costs nothing on the puff cells that do not need it.
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/* ========================================================================== *
 * GPU-simulated sprite batch
 * ========================================================================== */

const STRIDES = { iOrigin: 3, iVel: 3, iLife: 4, iSize: 4, iCol0: 4, iCol1: 4, iOpt: 4 };

class ParticleBatch {
  constructor(capacity, material) {
    this.capacity = capacity;
    this.head = 0;
    this.wrapped = false;
    this.lastExpiry = 0;

    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0], 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.instanceCount = 0;

    this.arrays = {};
    this.attrs = {};
    for (const name in STRIDES) {
      const arr = new Float32Array(capacity * STRIDES[name]);
      const attr = new THREE.InstancedBufferAttribute(arr, STRIDES[name]);
      attr.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute(name, attr);
      this.arrays[name] = arr;
      this.attrs[name] = attr;
    }

    // Sprites are placed in world space by the shader; the mesh never moves, so an
    // infinite bound is correct and skips a per-frame bounds recompute.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.geometry = geo;
    this.mesh = new THREE.Mesh(geo, material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
    this.mesh.matrixAutoUpdate = false;

    this._lo = -1;
    this._hi = -1;
  }

  /** Reserves the next slot in the ring. Oldest in flight is overwritten on wrap. */
  alloc() {
    const i = this.head;
    this.head++;
    if (this.head >= this.capacity) {
      this.head = 0;
      this.wrapped = true;
      this._lo = 0;
      this._hi = this.capacity - 1;
    }
    if (this._lo < 0 || i < this._lo) this._lo = i;
    if (i > this._hi) this._hi = i;
    return i;
  }

  flush(now) {
    if (this._lo < 0) return;
    const count = this._hi - this._lo + 1;
    for (const name in STRIDES) {
      const s = STRIDES[name];
      const attr = this.attrs[name];
      attr.addUpdateRange(this._lo * s, count * s);
      attr.needsUpdate = true;
    }
    this._lo = this._hi = -1;
    this.geometry.instanceCount = this.wrapped ? this.capacity : this.head;
    void now;
  }

  /** Everything in flight has expired — collapse the draw range back to nothing. */
  maybeReset(now) {
    if (now > this.lastExpiry && this.geometry.instanceCount !== 0) {
      this.geometry.instanceCount = 0;
      this.head = 0;
      this.wrapped = false;
    }
  }
}

/* ========================================================================== *
 * CPU-physics debris — real geometry, real bounces
 * ========================================================================== */

const D = { PX: 0, PY: 1, PZ: 2, VX: 3, VY: 4, VZ: 5, RX: 6, RY: 7, RZ: 8, WX: 9, WY: 10, WZ: 11, SX: 12, SY: 13, SZ: 14, AGE: 15, LIFE: 16, BOUNCE: 17, DRAG: 18, REST: 19 };
const D_STRIDE = 20;

class DebrisPool {
  /**
   * @param {boolean} align orient each piece along its velocity and stretch it by
   *   speed, instead of tumbling it. Sparks streak; chips tumble.
   */
  constructor(capacity, geometry, material, physics, align = false, stretch = 0) {
    this.capacity = capacity;
    this.physics = physics;
    this.align = align;
    this.stretch = stretch;
    this.count = 0;
    this.data = new Float32Array(capacity * D_STRIDE);
    this.colors = new Float32Array(capacity * 3);
    this.colorsEnd = new Float32Array(capacity * 3);

    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    // Allocating the colour buffer up front avoids a mid-firefight reallocation.
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);

    this._p = new THREE.Vector3();
    this._q = new THREE.Quaternion();
    this._s = new THREE.Vector3();
    this._e = new THREE.Euler();
    this._m = new THREE.Matrix4();
    this._dir = new THREE.Vector3();
    this._up = new THREE.Vector3(0, 1, 0);
    this._rayCursor = 0;
  }

  spawn(x, y, z, vx, vy, vz, sx, sy, sz, life, r, g, b, drag, restitution, r2, g2, b2) {
    // Full pool: recycle slot 0 rather than dropping the spawn, so a big impact
    // always shows debris even if the pool is saturated.
    const i = this.count < this.capacity ? this.count++ : 0;
    const d = this.data;
    const o = i * D_STRIDE;
    d[o + D.PX] = x; d[o + D.PY] = y; d[o + D.PZ] = z;
    d[o + D.VX] = vx; d[o + D.VY] = vy; d[o + D.VZ] = vz;
    d[o + D.RX] = Math.random() * 6.283; d[o + D.RY] = Math.random() * 6.283; d[o + D.RZ] = Math.random() * 6.283;
    d[o + D.WX] = (Math.random() - 0.5) * 34; d[o + D.WY] = (Math.random() - 0.5) * 34; d[o + D.WZ] = (Math.random() - 0.5) * 34;
    d[o + D.SX] = sx; d[o + D.SY] = sy; d[o + D.SZ] = sz;
    d[o + D.AGE] = 0; d[o + D.LIFE] = life; d[o + D.BOUNCE] = 0;
    d[o + D.DRAG] = drag; d[o + D.REST] = restitution;
    const c = i * 3;
    this.colors[c] = r; this.colors[c + 1] = g; this.colors[c + 2] = b;
    this.colorsEnd[c] = r2 === undefined ? r : r2;
    this.colorsEnd[c + 1] = g2 === undefined ? g : g2;
    this.colorsEnd[c + 2] = b2 === undefined ? b : b2;
    return i;
  }

  update(dt) {
    const d = this.data;
    const g = TUNING.debrisGravity;
    let rays = 0;
    const rayBudget = TUNING.debrisRaycastsPerFrame;

    for (let i = 0; i < this.count; ) {
      const o = i * D_STRIDE;
      d[o + D.AGE] += dt;
      if (d[o + D.AGE] >= d[o + D.LIFE]) {
        // Swap-remove: instance data is rewritten every frame anyway, so this is free.
        this.count--;
        if (i !== this.count) {
          const j = this.count * D_STRIDE;
          for (let k = 0; k < D_STRIDE; k++) d[o + k] = d[j + k];
          const ci = i * 3;
          const cj = this.count * 3;
          this.colors[ci] = this.colors[cj];
          this.colors[ci + 1] = this.colors[cj + 1];
          this.colors[ci + 2] = this.colors[cj + 2];
          this.colorsEnd[ci] = this.colorsEnd[cj];
          this.colorsEnd[ci + 1] = this.colorsEnd[cj + 1];
          this.colorsEnd[ci + 2] = this.colorsEnd[cj + 2];
        }
        continue;
      }

      const damp = Math.exp(-d[o + D.DRAG] * dt);
      d[o + D.VY] -= g * dt;
      d[o + D.VX] *= damp; d[o + D.VY] *= damp; d[o + D.VZ] *= damp;

      let sx = d[o + D.VX] * dt;
      let sy = d[o + D.VY] * dt;
      let sz = d[o + D.VZ] * dt;
      const len = Math.hypot(sx, sy, sz);

      // Collide only while a piece is still lively. Once it has settled or bounced
      // a few times it stops paying for raycasts.
      if (this.physics && len > 1e-4 && d[o + D.BOUNCE] < 3 && rays < rayBudget) {
        rays++;
        this._dir.set(sx / len, sy / len, sz / len);
        this._p.set(d[o + D.PX], d[o + D.PY], d[o + D.PZ]);
        const hit = this.physics.raycast(this._p, this._dir, len + 0.02, MASK.WORLD | MASK.PROP);
        if (hit && hit.hit) {
          const n = hit.normal;
          const rest = d[o + D.REST];
          d[o + D.PX] = hit.point.x + n.x * 0.012;
          d[o + D.PY] = hit.point.y + n.y * 0.012;
          d[o + D.PZ] = hit.point.z + n.z * 0.012;
          const vd = d[o + D.VX] * n.x + d[o + D.VY] * n.y + d[o + D.VZ] * n.z;
          // Reflect, lose energy, and scatter the tangent so a burst does not all
          // ricochet along the same line.
          d[o + D.VX] = (d[o + D.VX] - 2 * vd * n.x) * rest + (Math.random() - 0.5) * 0.6;
          d[o + D.VY] = (d[o + D.VY] - 2 * vd * n.y) * rest + (Math.random() - 0.5) * 0.4;
          d[o + D.VZ] = (d[o + D.VZ] - 2 * vd * n.z) * rest + (Math.random() - 0.5) * 0.6;
          d[o + D.BOUNCE] += 1;
          d[o + D.WX] *= 0.55; d[o + D.WY] *= 0.55; d[o + D.WZ] *= 0.55;
          sx = sy = sz = 0;
        }
      }

      d[o + D.PX] += sx; d[o + D.PY] += sy; d[o + D.PZ] += sz;
      d[o + D.RX] += d[o + D.WX] * dt; d[o + D.RY] += d[o + D.WY] * dt; d[o + D.RZ] += d[o + D.WZ] * dt;

      // Shrink away over the last fifth of the lifetime instead of vanishing.
      const k = d[o + D.AGE] / d[o + D.LIFE];
      const shrink = k > 0.8 ? 1 - (k - 0.8) / 0.2 : 1;

      this._p.set(d[o + D.PX], d[o + D.PY], d[o + D.PZ]);
      if (this.align) {
        // Streak along the direction of travel and stretch with speed, so a shower
        // of ricochets reads as motion rather than a cloud of dots.
        const sp = Math.hypot(d[o + D.VX], d[o + D.VY], d[o + D.VZ]);
        if (sp > 1e-3) {
          this._dir.set(d[o + D.VX] / sp, d[o + D.VY] / sp, d[o + D.VZ] / sp);
          this._q.setFromUnitVectors(this._up, this._dir);
        }
        const stretch = 1 + sp * this.stretch;
        this._s.set(d[o + D.SX] * shrink, d[o + D.SY] * shrink * stretch, d[o + D.SZ] * shrink);
      } else {
        this._e.set(d[o + D.RX], d[o + D.RY], d[o + D.RZ]);
        this._q.setFromEuler(this._e);
        this._s.set(d[o + D.SX] * shrink, d[o + D.SY] * shrink, d[o + D.SZ] * shrink);
      }
      this._m.compose(this._p, this._q, this._s);
      this.mesh.setMatrixAt(i, this._m);
      const c = i * 3;
      // Sparks cool as they fly: interpolate towards the end colour over the life.
      const ic = this.mesh.instanceColor.array;
      ic[c] = this.colors[c] + (this.colorsEnd[c] - this.colors[c]) * k;
      ic[c + 1] = this.colors[c + 1] + (this.colorsEnd[c + 1] - this.colors[c + 1]) * k;
      ic[c + 2] = this.colors[c + 2] + (this.colorsEnd[c + 2] - this.colors[c + 2]) * k;
      i++;
    }

    this.mesh.count = this.count;
    if (this.count > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.mesh.instanceColor.needsUpdate = true;
    }
  }
}

/* ========================================================================== *
 * Scene depth prepass (for soft particles)
 * ========================================================================== */

/**
 * Renders scene depth into a low-resolution target so smoke can fade where it
 * intersects the world. It runs only on frames where a soft particle is actually
 * alive, at a third of the display resolution, with colour writes disabled — a
 * depth-only pass over static geometry is close to free, and the alternative
 * (sampling the main framebuffer's depth while writing to it) is a feedback loop.
 */
class DepthPrepass {
  constructor(ctx, hideRoot) {
    this.ctx = ctx;
    this.hideRoot = hideRoot;
    this.enabled = ctx.quality.level !== 'medium';
    this.failed = false;

    const w = Math.max(160, Math.floor(window.innerWidth / TUNING.depthScale));
    const h = Math.max(120, Math.floor(window.innerHeight / TUNING.depthScale));

    /**
     * Two targets, alternated. The particles sample the depth written *last*
     * frame while this frame's is being filled.
     *
     * This is not a micro-optimisation. Writing a depth target and then sampling
     * it later in the same frame is a read-after-write hazard: the driver has to
     * finish and resolve the prepass before the transparent pass can read it,
     * which on a tile-based GPU means flushing the whole render pass. Measured on
     * the real level that single dependency cost ~9.6 ms a frame — thirty times
     * the cost of the pass itself. One frame of latency in a smoke fade is
     * invisible; a nine-millisecond stall is not.
     */
    const mkRT = () => {
      const depth = new THREE.DepthTexture(w, h);
      depth.type = THREE.UnsignedIntType;
      depth.minFilter = THREE.NearestFilter;
      depth.magFilter = THREE.NearestFilter;
      return new THREE.WebGLRenderTarget(w, h, {
        depthTexture: depth,
        depthBuffer: true,
        generateMipmaps: false,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
      });
    };
    this.rts = [mkRT(), mkRT()];
    this.index = 0;
    // Until the first pass lands, hand out a target cleared to the far plane so
    // the fade is a no-op rather than erasing everything.
    this.readTexture = this.rts[1].depthTexture;
    this._primed = false;
    this.override = new THREE.MeshBasicMaterial({ colorWrite: false });
    this._w = 0;
    this._h = 0;

    // `scene.overrideMaterial` forces depthWrite on for everything it touches, so
    // the sky dome and any transparent world geometry would otherwise stamp bogus
    // depth and make all smoke fade to nothing. Collect the objects that opt out of
    // depth writes and hide them for the pass. Refreshed lazily: scene contents
    // change rarely, and a full traversal every frame is not worth it.
    this.skips = [];
    this._skipVis = [];
    this._skipTimer = 99;
    this._frame = 0;
  }

  /** True on the frames the prepass should actually run. */
  due() {
    return this._frame++ % TUNING.depthInterval === 0;
  }

  _collectSkips(dt) {
    this._skipTimer += dt;
    if (this._skipTimer < 2) return;
    this._skipTimer = 0;
    const list = this.skips;
    list.length = 0;
    this.ctx.scene.traverse((o) => {
      if (!o.isMesh && !o.isPoints && !o.isLine) return;
      const m = o.material;
      if (!m) return;
      const noDepth = Array.isArray(m) ? m.every((x) => x.depthWrite === false) : m.depthWrite === false;
      if (noDepth) list.push(o);
    });
  }

  resize() {
    const w = Math.max(160, Math.floor(window.innerWidth / TUNING.depthScale));
    const h = Math.max(120, Math.floor(window.innerHeight / TUNING.depthScale));
    if (w === this._w && h === this._h) return;
    this._w = w;
    this._h = h;
    this.rts[0].setSize(w, h);
    this.rts[1].setSize(w, h);
    this._primed = false;
  }

  render(dt) {
    if (!this.enabled || this.failed) return;
    const { renderer, scene, camera } = this.ctx;
    const prevOverride = scene.overrideMaterial;
    const prevTarget = renderer.getRenderTarget();
    const wasVisible = this.hideRoot.visible;
    const skips = this.skips;
    try {
      this.resize();
      this._collectSkips(dt);

      // Write this frame's depth into one target; publish the other, which was
      // filled last frame and is long since resolved, for the particles to sample.
      const write = this.rts[this.index];
      this.index ^= 1;
      const read = this.rts[this.index];
      if (this._primed) this.readTexture = read.depthTexture;

      this.hideRoot.visible = false;
      for (let i = 0; i < skips.length; i++) {
        this._skipVis[i] = skips[i].visible;
        skips[i].visible = false;
      }
      scene.overrideMaterial = this.override;
      renderer.setRenderTarget(write);
      renderer.clear(true, true, false);
      renderer.render(scene, camera);
      this._primed = true;
    } catch (err) {
      console.warn('[fx] depth prepass failed, soft particles disabled', err);
      this.failed = true;
    } finally {
      renderer.setRenderTarget(prevTarget);
      scene.overrideMaterial = prevOverride;
      this.hideRoot.visible = wasVisible;
      for (let i = 0; i < skips.length; i++) skips[i].visible = this._skipVis[i] !== false;
    }
  }
}

/* ========================================================================== *
 * Zero-allocation spawn builder
 * ========================================================================== */

class Emitter {
  constructor(sys) {
    this.sys = sys;
    this.batch = null;
    this.ox = 0; this.oy = 0; this.oz = 0;
    this.vx = 0; this.vy = 0; this.vz = 0;
    this._life = 1; this._drag = 1; this._grav = 1;
    this._s0 = 0.2; this._s1 = 0.4; this._roll = 0; this._rollV = 0;
    this.r0 = 1; this.g0 = 1; this.b0 = 1; this._a = 1;
    this.r1 = 1; this.g1 = 1; this.b1 = 1; this._atlas = 0;
    this._shade = 0; this._stretch = 0; this._fadeIn = 0.1; this._fadeOut = 1.5;
  }

  reset(batch) {
    this.batch = batch;
    this.ox = this.oy = this.oz = 0;
    this.vx = this.vy = this.vz = 0;
    this._life = 1; this._drag = 1; this._grav = 1;
    this._s0 = 0.2; this._s1 = 0.4; this._roll = 0; this._rollV = 0;
    this.r0 = this.g0 = this.b0 = 1; this._a = 1;
    this.r1 = this.g1 = this.b1 = 1; this._atlas = 0;
    this._shade = 0; this._stretch = 0; this._fadeIn = 0.1; this._fadeOut = 1.5;
    return this;
  }

  pos(x, y, z) { this.ox = x; this.oy = y; this.oz = z; return this; }
  vel(x, y, z) { this.vx = x; this.vy = y; this.vz = z; return this; }
  life(v) { this._life = v; return this; }
  drag(v) { this._drag = v; return this; }
  grav(v) { this._grav = v; return this; }
  size(a, b) { this._s0 = a; this._s1 = b === undefined ? a : b; return this; }
  roll(r, v) { this._roll = r; this._rollV = v || 0; return this; }
  colA(r, g, b, a) { this.r0 = r; this.g0 = g; this.b0 = b; if (a !== undefined) this._a = a; return this; }
  colB(r, g, b) { this.r1 = r; this.g1 = g; this.b1 = b; return this; }
  col(r, g, b, a) { return this.colA(r, g, b, a).colB(r, g, b); }
  alpha(a) { this._a = a; return this; }
  atlas(i) { this._atlas = i; return this; }
  shade(s) { this._shade = s; return this; }
  stretch(s) { this._stretch = s; return this; }
  fade(inFrac, outPow) { this._fadeIn = inFrac; this._fadeOut = outPow; return this; }

  go() {
    const b = this.batch;
    const i = b.alloc();
    const A = b.arrays;
    let o = i * 3;
    A.iOrigin[o] = this.ox; A.iOrigin[o + 1] = this.oy; A.iOrigin[o + 2] = this.oz;
    A.iVel[o] = this.vx; A.iVel[o + 1] = this.vy; A.iVel[o + 2] = this.vz;
    o = i * 4;
    const now = this.sys.time;
    A.iLife[o] = now; A.iLife[o + 1] = this._life; A.iLife[o + 2] = this._drag; A.iLife[o + 3] = this._grav;
    A.iSize[o] = this._s0; A.iSize[o + 1] = this._s1; A.iSize[o + 2] = this._roll; A.iSize[o + 3] = this._rollV;
    A.iCol0[o] = this.r0; A.iCol0[o + 1] = this.g0; A.iCol0[o + 2] = this.b0; A.iCol0[o + 3] = this._a;
    A.iCol1[o] = this.r1; A.iCol1[o + 1] = this.g1; A.iCol1[o + 2] = this.b1; A.iCol1[o + 3] = this._atlas;
    A.iOpt[o] = this._shade; A.iOpt[o + 1] = this._stretch; A.iOpt[o + 2] = this._fadeIn; A.iOpt[o + 3] = this._fadeOut;
    const expiry = now + this._life;
    if (expiry > b.lastExpiry) b.lastExpiry = expiry;
    return i;
  }
}

/* ========================================================================== *
 * ParticleSystem
 * ========================================================================== */

export class ParticleSystem {
  constructor(ctx, root) {
    this.ctx = ctx;
    this.root = root;
    this.time = 0;

    const budget = ctx.quality.maxParticles ?? 2000;
    const atlas = makeParticleAtlas(TUNING.atlasCell);
    this.atlas = atlas;

    const commonUniforms = {
      uTime: { value: 0 },
      uAtlas: { value: atlas },
      uAtlasGrid: { value: new THREE.Vector2(ATLAS_COLS, ATLAS_ROWS) },
      uNearFade: { value: new THREE.Vector2(TUNING.nearFadeStart, TUNING.nearFadeEnd) },
      uPxScale: { value: 0.002 },
      uMinPixels: { value: TUNING.minSparkPixels },
      // Read by the vertex stage of both materials, so it lives in the shared set
      // even though only the soft material's fragment stage consumes it.
      uSoftDist: { value: TUNING.softDistance },
      uSunDirView: { value: new THREE.Vector3(0, 0, 1) },
      uSunColor: { value: new THREE.Color(1.35, 1.2, 1.0) },
      uAmbient: { value: new THREE.Color(0.30, 0.36, 0.45) },
    };
    this.uniforms = commonUniforms;

    this.depth = new DepthPrepass(ctx, root);

    const softUniforms = Object.assign({}, commonUniforms, {
      uDepth: { value: this.depth.readTexture },
      uInvRes: { value: new THREE.Vector2(1 / window.innerWidth, 1 / window.innerHeight) },
      uCamNear: { value: ctx.camera.near },
      uCamFar: { value: ctx.camera.far },
    });
    // Shared uniform objects so one update drives both materials.
    for (const k in commonUniforms) softUniforms[k] = commonUniforms[k];
    this.softUniforms = softUniforms;

    const softMat = new THREE.ShaderMaterial({
      vertexShader: particleVert,
      fragmentShader: particleFrag,
      uniforms: softUniforms,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor, // premultiplied alpha
      blendDst: THREE.OneMinusSrcAlphaFactor,
      side: THREE.DoubleSide,
      defines: this.depth.enabled ? { SOFT: '' } : {},
    });

    // The additive pass needs the depth fade every bit as much as the alpha one.
    // A fireball is a screen-facing quad three metres across; where it crosses the
    // road, `depthTest` cuts it on a straight line and leaves a *rectangle* of fire
    // sitting on the tarmac — the sprite's own silhouette is gone and its quad is
    // showing. Sharing the soft uniform objects (not copies of their values) means
    // the one rebind in `update()` drives both materials.
    const addUniforms = Object.assign({}, softUniforms);

    const addMat = new THREE.ShaderMaterial({
      vertexShader: particleVert,
      fragmentShader: particleFrag,
      uniforms: addUniforms,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      // Additive, but the shader already outputs premultiplied colour, so the
      // source factor must be One rather than SrcAlpha or it squares the alpha.
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      side: THREE.DoubleSide,
      defines: this.depth.enabled ? { SOFT: '' } : {},
    });

    this.softMat = softMat;
    this.addMat = addMat;

    this.soft = new ParticleBatch(Math.floor(budget * TUNING.softShare), softMat);
    this.add = new ParticleBatch(Math.floor(budget * TUNING.addShare), addMat);
    this.add.mesh.renderOrder = 11;
    root.add(this.soft.mesh, this.add.mesh);

    /* ---- CPU-physics debris ---- */
    const scale = ctx.quality.level === 'medium' ? 0.5 : ctx.quality.level === 'high' ? 0.75 : 1;
    const chipGeo = makeChunkGeometry();
    const shardGeo = makeShardGeometry();
    const shellGeo = new THREE.CylinderGeometry(0.5, 0.42, 2.2, 7, 1);
    const emberGeo = new THREE.CylinderGeometry(0.5, 0.5, 1.0, 4, 1);

    const chipMat = new THREE.MeshStandardMaterial({ roughness: 0.92, metalness: 0.0, color: 0xffffff });
    const shardMat = new THREE.MeshStandardMaterial({ roughness: 0.14, metalness: 0.55, color: 0xffffff, envMapIntensity: 2.0 });
    const shellMat = new THREE.MeshStandardMaterial({ roughness: 0.28, metalness: 1.0, color: 0xffffff, envMapIntensity: 1.6 });
    // Embers are emissive, not lit: a ricocheting spark is incandescent metal and
    // has to stay bright in shadow and blow out into the bloom pass.
    const emberMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      toneMapped: true,
      fog: false,
    });

    this.chips = new DebrisPool(Math.max(24, Math.floor(TUNING.chipCount * scale)), chipGeo, chipMat, ctx.physics);
    this.shards = new DebrisPool(Math.max(16, Math.floor(TUNING.shardCount * scale)), shardGeo, shardMat, ctx.physics);
    this.shells = new DebrisPool(TUNING.shellCount, shellGeo, shellMat, ctx.physics);
    // Stretch 0.14: an ember's streak length is `1 + speed * stretch` times its
    // base length, so at 18 m/s it is 3.5x as long as it is at rest. That ratio is
    // the whole point — a ricochet that leaves the wall fast, arcs, slows and lands
    // as a short dull dash reads as one object losing energy. A fixed length reads
    // as a decal sliding around the room.
    this.embers = new DebrisPool(Math.max(24, Math.floor(TUNING.emberCount * scale)), emberGeo, emberMat, ctx.physics, true, 0.14);
    this.embers.mesh.renderOrder = 13;
    root.add(this.chips.mesh, this.shards.mesh, this.shells.mesh, this.embers.mesh);

    this.emitter = new Emitter(this);
    this._sunDir = new THREE.Vector3(0.5, 0.8, 0.35).normalize();
    this._sunView = new THREE.Vector3();
    this._resetTimer = 0;
  }

  /** Begin a soft (alpha, lit, depth-faded) particle. */
  beginSoft() { return this.emitter.reset(this.soft).shade(1); }
  /** Begin an additive particle. */
  beginAdd() { return this.emitter.reset(this.add).shade(0); }
  /** Begin an alpha-blended but unlit particle (blood droplets, grit). */
  beginAlpha() { return this.emitter.reset(this.soft).shade(0); }

  setSun(dir, color, ambient) {
    if (dir) this._sunDir.copy(dir).normalize();
    if (color) this.uniforms.uSunColor.value.copy(color);
    if (ambient) this.uniforms.uAmbient.value.copy(ambient);
  }

  update(dt, elapsed) {
    this.time = elapsed;
    this.uniforms.uTime.value = elapsed;

    // Sun direction in view space, for the smoke normal-map lighting.
    this._sunView.copy(this._sunDir).transformDirection(this.ctx.camera.matrixWorldInverse);
    this.uniforms.uSunDirView.value.copy(this._sunView);

    // World units per pixel at one metre, so the shader can keep thin stretched
    // sparks from falling below a pixel and breaking into dashes.
    const cam = this.ctx.camera;
    this.uniforms.uPxScale.value = (2 * Math.tan((cam.fov * Math.PI) / 360)) / window.innerHeight;

    this.soft.flush(elapsed);
    this.add.flush(elapsed);

    this.chips.update(dt);
    this.shards.update(dt);
    this.shells.update(dt);
    this.embers.update(dt);

    // Every half second, collapse batches whose particles have all expired so we
    // stop feeding the vertex shader dead instances between firefights.
    this._resetTimer += dt;
    if (this._resetTimer > 0.5) {
      this._resetTimer = 0;
      this.soft.maybeReset(elapsed);
      this.add.maybeReset(elapsed);
    }

    if (this.depth.enabled) {
      this.softUniforms.uInvRes.value.set(1 / window.innerWidth, 1 / window.innerHeight);
      this.softUniforms.uCamNear.value = this.ctx.camera.near;
      this.softUniforms.uCamFar.value = this.ctx.camera.far;
      if (elapsed < this.soft.lastExpiry && this.depth.due()) {
        this.depth.render(dt);
        // The prepass alternates targets, so rebind whichever one is now safe to read.
        this.softUniforms.uDepth.value = this.depth.readTexture;
      }
    }
  }

  get liveEstimate() {
    return (this.soft.wrapped ? this.soft.capacity : this.soft.head) + (this.add.wrapped ? this.add.capacity : this.add.head);
  }

  dispose() {
    this.soft.geometry.dispose();
    this.add.geometry.dispose();
    this.softMat.dispose();
    this.addMat.dispose();
    this.atlas.dispose();
    for (const rt of this.depth.rts) rt.dispose();
  }
}

/* ------------------------------- geometry -------------------------------- */

/** Irregular chunk: an octahedron with its vertices kicked around. */
function makeChunkGeometry() {
  const g = new THREE.OctahedronGeometry(0.5, 0);
  const p = g.attributes.position;
  let s = 12345;
  const rnd = () => ((s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  for (let i = 0; i < p.count; i++) {
    p.setXYZ(i, p.getX(i) * (0.6 + rnd() * 0.8), p.getY(i) * (0.6 + rnd() * 0.8), p.getZ(i) * (0.6 + rnd() * 0.8));
  }
  g.computeVertexNormals();
  return g;
}

/** Flat angular sliver, for glass and metal spall. */
function makeShardGeometry() {
  const g = new THREE.TetrahedronGeometry(0.6, 0);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i++) p.setZ(i, p.getZ(i) * 0.22);
  g.computeVertexNormals();
  return g;
}
